import { type DragEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type AssetDelivery,
  type Campaign,
  CONTENT_KINDS,
  FUNNEL_STAGES,
  rankCard,
  setContentArchived,
  updateCardFields,
} from '../api.ts';
import { useAssetDeliveries } from '../assets.ts';
import { type ActionDialog, type ActionHost, primaryAction } from '../board/actions.ts';
import { BulkBar } from '../board/BulkBar.tsx';
import { Calendar } from '../board/Calendar.tsx';
import { CampaignsDialog } from '../board/CampaignsDialog.tsx';
import { Card, CardFocusContext, CardSelectionContext } from '../board/Card.tsx';
import type { CardMenuHost } from '../board/CardMenu.tsx';
import { ActionDialogs } from '../board/dialogs.tsx';
import { Drawer, DuplicateDialog } from '../board/Drawer.tsx';
import { BOARD_SHOWS, FILTER_PARAMS, filterCards, filtersNarrow, readBoardFilters } from '../board/filters.ts';
import { firstOfColumn, type Grid, nextNeedingYou, stepInColumn, stepSideways } from '../board/keys.ts';
import { BOARD_GROUPS, type BoardGroup, buildLanes, isGroup, type Lane, laneOf, NO_LANE } from '../board/lanes.ts';
import { shortDay } from '../board/lines.ts';
import {
  BOARD_COLUMNS,
  type BoardCard,
  type BoardColumn,
  cardHref,
  cardTitle,
  groupByColumn,
  type ContentCard,
  needsYou,
} from '../board/model.ts';
import {
  type CalendarPeriod,
  periodOf,
  periodParams,
  readPeriod,
  stepPeriod,
  switchMode,
} from '../board/period.ts';
import { type CardActions, CardActionsContext } from '../board/PrimaryActionButton.tsx';
import {
  insertionNeighbours,
  isRankable,
  type Neighbours,
  rankBetween,
  type ReorderStep,
  stepNeighbours,
  withRank,
} from '../board/reorder.ts';
import {
  type BulkAction,
  type BulkInput,
  bulkSummary,
  pruneSelection,
  rangeInColumn,
  runBulk,
  toggleSelected,
} from '../board/selection.ts';
import { QuickAdd } from '../board/QuickAdd.tsx';
import { ShortcutsDialog } from '../board/ShortcutsDialog.tsx';
import { CARD_DATA_TYPE, LANE_CHANGE_REFUSED, type Move, transition } from '../board/transitions.ts';
import { useBoard } from '../board/useBoard.ts';
import { FUNNEL_LABEL } from '../BriefDetailsFields.tsx';
import { describeError, useAppData, useKeyChords } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { useNotifySwitch } from '../notifications.ts';
import { Link, navigate, replaceSearch, useLocation } from '../router.tsx';
import { useToast } from '../toast.tsx';
import { EmptyState, Kbd, PageHeader, Segmented, Skeleton } from '../ui.tsx';
import * as wording from '../wording.ts';

/**
 * Every piece of content work at once (US-014): seven columns from the idea to
 * the post, read from `GET /api/board` on the dashboard's beat. The server says
 * which column a card stands in; the order inside a column is decided here.
 * What each card shows is `board/Card.tsx` (US-015); with `group` set the
 * columns are cut into swimlanes (US-017, `board/lanes.ts`).
 */
export function Board() {
  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);
  // The calendar (US-032): `view=calendar`, and its month or week. Neither is a filter.
  const calendar = params.get('view') === 'calendar';
  const period = readPeriod(params, new Date());
  // `posted` and `archived` change what the server sends, so they are read
  // before the board is: the rest only narrow what it sent. The calendar shows
  // posts of any age, so it needs every posted content to filter and open them.
  const { board, error, refresh } = useBoard({
    archived: params.get('archived') === '1',
    postedAll: params.get('posted') === 'all' || calendar,
  });
  const { stats, repositories, refresh: refreshAppData } = useAppData();
  const toast = useToast();
  const filters = readBoardFilters(params, board?.campaigns ?? null, board?.brandBooks ?? null);
  // Not a filter: Clear filters leaves the grouping as it is.
  const groupParam = params.get('group');
  const group = isGroup(groupParam) ? groupParam : null;
  // Nor is the open card (US-018): it is looked up on the whole board, so a
  // card the filters hide keeps its drawer.
  const openCardId = params.get('card');
  const [query, setQuery] = useState(() => params.get('q') ?? readLastQuery(search));
  const searchRef = useRef<HTMLInputElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // A bare `/board` — opened, reloaded or picked from the sidebar while on the
  // board — comes back to the last view; any other address is that view. The
  // toolbar remembers what it writes before writing it, so emptying the
  // address from here is never undone.
  useEffect(() => {
    if (pathname !== '/board') return;
    if (search === '') {
      const last = readLastSearch();
      if (last !== null && last !== '') replaceSearch(new URLSearchParams(last));
      return;
    }
    writeLastSearch(search.replace(/^\?/, ''));
  }, [pathname, search]);

  const writeSearch = (next: URLSearchParams): void => {
    writeLastSearch(next.toString());
    replaceSearch(next);
  };

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(window.location.search);
    // 'all' is the default of every select and of Show, '' of the search and of
    // the checkboxes: none of them belongs in the address.
    if (value === '' || value === 'all') next.delete(key);
    else next.set(key, value);
    writeSearch(next);
  };

  // The search box writes to the URL a beat after typing stops, like the
  // Content page, so a reload keeps it without a history entry per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setParam('q', query.trim());
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [query]);

  /** The drawer to open on the planning conversation's reply box (US-023), once per ask. */
  const [planningFocus, setPlanningFocus] = useState<{ cardId: string; token: number } | null>(null);
  /** The drawer to open on the campaign field (`e`, US-027), once per press. */
  const [campaignFocus, setCampaignFocus] = useState<{ cardId: string; token: number } | null>(null);
  /**
   * The card the keys act on (US-027), kept by id so it survives a poll. A
   * card the filters hide, or that left the board, hands it to the first card.
   */
  const [focusedId, setFocusedId] = useState<string | null>(() => params.get('card'));
  const [showKeys, setShowKeys] = useState(false);
  /** The Campaigns dialog (US-030). */
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);

  const openCard = (cardId: string): void => {
    setPlanningFocus(null);
    setCampaignFocus(null);
    setFocusedId(cardId);
    setParam('card', cardId);
  };

  const closeCard = useCallback((): void => {
    const next = new URLSearchParams(window.location.search);
    const cardId = next.get('card');
    next.delete('card');
    setPlanningFocus(null);
    setCampaignFocus(null);
    writeLastSearch(next.toString());
    replaceSearch(next);
    // Back to the card the drawer was about, once the board has drawn without it.
    if (cardId !== null) {
      window.requestAnimationFrame(() => {
        document.querySelector<HTMLElement>(`[data-card-id="${CSS.escape(cardId)}"]`)?.focus();
      });
    }
  }, []);

  // The primary action of a card (US-019): one dialog at a time for the whole
  // board, and the cards whose action is running, which ignore a second click.
  const [dialog, setDialog] = useState<{
    dialog: ActionDialog;
    card: BoardCard;
    /** Runs once the dialog has done its work: a move's announcement (US-025). */
    onMoved?: () => void;
    /** The calendar day a To post card was dropped on (US-033). */
    day?: string;
  } | null>(null);
  const [running, setRunning] = useState<ReadonlySet<string>>(() => new Set());
  const runningRef = useRef(new Set<string>());

  /** Marks a card as having work running, or done with it. */
  const markRunning = (cardId: string, on: boolean): void => {
    if (on) runningRef.current.add(cardId);
    else runningRef.current.delete(cardId);
    setRunning(new Set(runningRef.current));
  };

  /** Bumped after every action, so the calendar reads again at once too. */
  const [calendarToken, setCalendarToken] = useState(0);
  const refreshAll = useCallback(async (): Promise<void> => {
    setCalendarToken((token) => token + 1);
    await Promise.all([refresh(), refreshAppData()]);
  }, [refresh, refreshAppData]);

  /** Board | Calendar (`v`); the period stays in the address for the next visit. */
  const setView = (next: 'board' | 'calendar'): void => {
    setParam('view', next === 'calendar' ? 'calendar' : '');
  };

  /** Shows another month or week: one of `month` and `week`, never both. */
  const setPeriod = (next: CalendarPeriod): void => {
    const query = new URLSearchParams(window.location.search);
    const { month, week } = periodParams(next);
    query.delete('month');
    query.delete('week');
    if (month !== null) query.set('month', month);
    if (week !== null) query.set('week', week);
    writeSearch(query);
  };

  /**
   * Runs `work` for a card: a primary action, or a drop (US-025). A card with
   * work running ignores more; a throw is a `toast.error`. A dialog opened on
   * the way calls `onMoved` once it has done its work.
   */
  const runOn = (card: BoardCard, work: (host: ActionHost) => Promise<void>, onMoved?: () => void): void => {
    if (runningRef.current.has(card.cardId)) return;
    const host: ActionHost = {
      toast,
      refresh: refreshAll,
      navigate,
      openDrawer: (cardId, focus) => {
        // Answer Claude: the drawer opens on the planning conversation's reply box.
        openCard(cardId);
        if (focus === 'planning') setPlanningFocus({ cardId, token: Date.now() });
      },
      openDialog: (kind, target) => {
        setDialog({ dialog: kind, card: target, ...(onMoved === undefined ? {} : { onMoved }) });
      },
    };
    const mark = (on: boolean): void => {
      markRunning(card.cardId, on);
    };
    mark(true);
    work(host)
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
      })
      .finally(() => {
        mark(false);
      });
  };

  const runAction = (card: BoardCard): void => {
    const action = primaryAction(card);
    if (action !== null) runOn(card, action.run);
  };

  // Reordering by hand (US-026): the rank a card was just given shows at
  // once, and stays until the board has read the server's, or is dropped
  // again when the call fails, putting the card back where it was.
  const [ranked, setRanked] = useState<ReadonlyMap<string, number>>(() => new Map());
  /** The cards a drop would land between in the list under the pointer, and where its line is drawn. */
  const [insertion, setInsertion] = useState<{ list: string; index: number } | null>(null);
  const insertionRef = useRef<{ list: string; index: number; neighbours: Neighbours | null } | null>(null);
  const [duplicating, setDuplicating] = useState<ContentCard | null>(null);

  const reorder = (card: BoardCard, neighbours: Neighbours, list: readonly BoardCard[]): void => {
    const rank = rankBetween(neighbours);
    if (rank === null) return;
    const { above, below } = neighbours;
    const rest = list.filter((candidate) => candidate.cardId !== card.cardId);
    const position = above === null ? 1 : rest.indexOf(above) + 2;
    const forget = (): void => {
      setRanked((previous) => {
        const next = new Map(previous);
        next.delete(card.cardId);
        return next;
      });
    };
    runOn(card, async () => {
      setRanked((previous) => new Map(previous).set(card.cardId, rank));
      try {
        await rankCard(card.cardId, { aboveCardId: above?.cardId ?? null, belowCardId: below?.cardId ?? null });
        await refresh();
        announce(wording.boardReordered(cardTitle(card), position, list.length, columnLabel(card.column)));
      } catch (cause: unknown) {
        toast.error(`${wording.REORDER_FAILED} ${describeError(cause)}`);
      } finally {
        forget();
      }
    });
  };

  const archive = (card: ContentCard): void => {
    const { content } = card;
    const archiving = content.archivedAt === null;
    runOn(card, async () => {
      await setContentArchived(content.id, archiving);
      toast.ok(archiving ? wording.boardArchived(content.name) : wording.boardUnarchived(content.name), {
        label: wording.UNDO,
        onClick: () => {
          void setContentArchived(content.id, !archiving)
            .then(refreshAll)
            .catch((cause: unknown) => {
              toast.error(describeError(cause));
            });
        },
      });
      // Off the board unless Archived is ticked: its drawer would have nothing to show.
      if (archiving && !filters.archived && openCardId === card.cardId) closeCard();
      await refreshAll();
    });
  };

  // Dragging a card (US-025). The card is held here because a drag's data
  // cannot be read before the drop; every column asks `verdictOf` what a drop
  // on it would mean. A refused drop never fires `drop` (its effect is
  // `none`), so the last refused column hovered is kept for `dragend` to say why.
  const [dragging, setDragging] = useState<BoardCard | null>(null);
  const refusedOverRef = useRef<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  /** Says a move out loud; cleared first, so the same words twice are read twice. */
  const announce = (text: string): void => {
    setAnnouncement('');
    window.setTimeout(() => {
      setAnnouncement(text);
    }, 50);
  };

  const verdictOf = (card: BoardCard, column: BoardColumn, lane: Lane | null): Verdict => {
    const laneChanged = group !== null && lane !== null && laneOf(card, group) !== lane.id;
    if (group !== null && laneChanged) {
      const refusal = LANE_CHANGE_REFUSED[group];
      if (refusal !== null) return { kind: 'refused', reason: refusal };
      if (lane !== null && lane.campaign !== null && lane.campaign.archivedAt !== null) {
        return { kind: 'refused', reason: wording.MOVE_LANE_ARCHIVED };
      }
    }
    const move = transition(card, column);
    if (move.kind === 'refused') return move;
    if (move.kind === 'same') return laneChanged ? { kind: 'move', lane, move: null } : { kind: 'same' };
    return { kind: 'move', lane: laneChanged ? lane : null, move };
  };

  const clearInsertion = (): void => {
    insertionRef.current = null;
    setInsertion(null);
  };

  /**
   * A column or lane cell as a place to drop a card. Inside its own list a
   * card is only reordered (US-026), and only in a rankable column: the line
   * goes where it would land, between the cards as displayed.
   */
  const dropTarget = (column: BoardColumn, lane: Lane | null, list: readonly BoardCard[]): DropTarget => {
    const verdict = dragging === null ? null : verdictOf(dragging, column, lane);
    const listKey = `${lane?.id ?? ''}:${column}`;
    const reorders = verdict?.kind === 'same' && isRankable(column);
    return {
      state: verdict === null || verdict.kind === 'same' ? null : verdict.kind === 'move' ? 'ok' : 'no',
      insertAt: insertion !== null && insertion.list === listKey ? insertion.index : null,
      onDragOver: (event) => {
        if (dragging === null || verdict === null) return;
        refusedOverRef.current = verdict.kind === 'refused' ? verdict.reason : null;
        if (verdict.kind === 'same' && !reorders) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = verdict.kind === 'refused' ? 'none' : 'move';
        if (!reorders) return;
        const items = [...event.currentTarget.querySelectorAll<HTMLElement>('.board__cards > li')];
        let index = items.findIndex((item) => {
          const box = item.getBoundingClientRect();
          return event.clientY < box.top + box.height / 2;
        });
        if (index === -1) index = items.length;
        const neighbours = insertionNeighbours(list, dragging.cardId, index);
        const current = insertionRef.current;
        if (current !== null && current.list === listKey && current.index === index) return;
        insertionRef.current = { list: listKey, index, neighbours };
        // Next to itself it would stay put: no line.
        setInsertion(neighbours === null ? null : { list: listKey, index });
      },
      onDragLeave: (event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        refusedOverRef.current = null;
        if (insertionRef.current?.list === listKey) clearInsertion();
      },
      onDrop: (event) => {
        event.preventDefault();
        refusedOverRef.current = null;
        const landing = insertionRef.current;
        clearInsertion();
        setDragging(null);
        const cardId = event.dataTransfer.getData(CARD_DATA_TYPE);
        const card = board?.cards.find((candidate) => candidate.cardId === cardId);
        if (card === undefined) return;
        const drop = verdictOf(card, column, lane);
        if (drop.kind === 'refused') toast.warn(drop.reason);
        if (drop.kind === 'same') {
          // Anywhere else in its own column, a drop does nothing.
          if (isRankable(column) && landing?.list === listKey && landing.neighbours !== null) {
            reorder(card, landing.neighbours, list);
          }
          return;
        }
        if (drop.kind === 'move') dropCard(card, column, drop);
      },
    };
  };

  /** Runs a drop that is allowed: the campaign first, then the column move. */
  const dropCard = (card: BoardCard, column: BoardColumn, drop: Extract<Verdict, { kind: 'move' }>): void => {
    const title = cardTitle(card);
    const { move, lane } = drop;
    const place = move === null ? (lane?.name ?? '') : columnLabel(column);
    const moved = (): void => {
      announce(wording.boardMoved(title, place));
    };
    runOn(
      card,
      async (host) => {
        if (lane !== null) {
          await updateCardFields(card.cardId, { campaignId: lane.id === NO_LANE ? null : lane.id });
          if (move === null || move.dialog !== null) await host.refresh();
        }
        if (move === null) {
          moved();
          return;
        }
        await move.run(host);
        // A dialog says when it is done; anything else is done now.
        if (move.dialog === null) moved();
      },
      moved,
    );
  };

  const dragHandlers = {
    onDragStart: (event: DragEvent<HTMLElement>): void => {
      const cardId = (event.target as HTMLElement).closest<HTMLElement>('[data-card-id]')?.dataset['cardId'];
      setDragging(board?.cards.find((card) => card.cardId === cardId) ?? null);
      refusedOverRef.current = null;
    },
    onDragEnd: (event: DragEvent<HTMLElement>): void => {
      const reason = refusedOverRef.current;
      refusedOverRef.current = null;
      clearInsertion();
      setDragging(null);
      if (reason !== null && event.dataTransfer.dropEffect === 'none') toast.warn(reason);
    },
  };

  const clearFilters = (): void => {
    const next = new URLSearchParams(window.location.search);
    for (const key of FILTER_PARAMS) next.delete(key);
    setQuery('');
    writeSearch(next);
  };

  const campaigns = useMemo(
    () => new Map((board?.campaigns ?? []).map((campaign) => [campaign.id, campaign])),
    [board],
  );
  // One clock per render, so every card agrees about what is due.
  const now = Date.now();
  const shown =
    board === null
      ? null
      : board.cards.map((card) => {
          const rank = ranked.get(card.cardId);
          return rank === undefined ? card : withRank(card, rank);
        });
  const cards = shown === null ? null : filterCards(shown, filters, campaigns, now);
  // The calendar keeps the entries of the cards the filters keep; Show is the board's own.
  const calendarFilters = { ...filters, show: 'all' as const };
  const calendarCards = calendar && shown !== null ? filterCards(shown, calendarFilters, campaigns, now) : null;
  const onCalendar = calendarCards === null ? null : new Set(calendarCards.map((card) => card.cardId));
  // Beside the calendar, the To post column under the same filters, in its order (US-033).
  const toPostOnCalendar =
    calendarCards === null
      ? []
      : groupByColumn(calendarCards)['to-post'].filter((card): card is ContentCard => card.type === 'content');
  const keepOnCalendar = (cardId: string): boolean =>
    onCalendar === null ||
    onCalendar.has(cardId) ||
    (!filtersNarrow(calendarFilters) && !(shown ?? []).some((card) => card.cardId === cardId));
  const columns = cards === null ? null : groupByColumn(cards);
  // A narrowed board hides the lanes the filters emptied; the whole board
  // shows every live campaign and brand book, quiet ones too.
  const lanes =
    board === null || cards === null || group === null
      ? null
      : buildLanes(cards, group, board.campaigns, board.brandBooks).filter(
          (lane) => lane.count > 0 || !filtersNarrow(filters),
        );

  /** The list a card is drawn in, as displayed: its column, or its cell of a lane. */
  const listOf = (card: BoardCard): readonly BoardCard[] => {
    if (lanes !== null && group !== null) {
      const lane = lanes.find((candidate) => candidate.id === laneOf(card, group));
      if (lane !== undefined) return lane.columns[card.column];
    }
    return columns?.[card.column] ?? [];
  };

  const menu: CardMenuHost = {
    open: (card) => {
      openCard(card.cardId);
    },
    moves: (card) =>
      BOARD_COLUMNS.map(({ id, label }) => {
        const verdict = verdictOf(card, id, null);
        return { column: id, label, refused: verdict.kind === 'refused' ? verdict.reason : null };
      }),
    moveTo: (card, column) => {
      const verdict = verdictOf(card, column, null);
      if (verdict.kind === 'refused') toast.warn(verdict.reason);
      if (verdict.kind === 'move') dropCard(card, column, verdict);
    },
    steps: (card) => {
      if (!isRankable(card.column)) return null;
      const list = listOf(card);
      const can = (step: ReorderStep): boolean => stepNeighbours(list, card.cardId, step) !== null;
      return { up: can('up'), down: can('down'), top: can('top'), bottom: can('bottom') };
    },
    reorder: (card, step) => {
      const list = listOf(card);
      const neighbours = stepNeighbours(list, card.cardId, step);
      if (neighbours !== null) reorder(card, neighbours, list);
    },
    duplicate: (card) => {
      if (card.type === 'content') setDuplicating(card);
    },
    archive: (card) => {
      if (card.type === 'content') archive(card);
    },
  };
  const cardActions: CardActions = {
    running,
    run: runAction,
    openDialog: (kind, target) => {
      setDialog({ dialog: kind, card: target });
    },
    menu,
  };

  // The keyboard (US-027, Design reference § Keyboard). The focus is one card
  // id; the grid the keys walk is read off the board as drawn, so lanes,
  // folded lanes and the filters are what the eye sees.
  const displayed = cards ?? [];
  /** Board order: columns left to right, each top to bottom, down through every lane. */
  const boardOrder = BOARD_COLUMNS.flatMap(({ id }) =>
    lanes !== null ? lanes.flatMap((lane) => lane.columns[id]) : (columns?.[id] ?? []),
  );
  const firstShown = boardOrder[0]?.cardId ?? null;
  const focusId =
    focusedId !== null && displayed.some((card) => card.cardId === focusedId) ? focusedId : firstShown;
  const focusedCard = displayed.find((card) => card.cardId === focusId);

  const cardElement = (cardId: string): HTMLElement | null =>
    pageRef.current?.querySelector<HTMLElement>(`.board-card[data-card-id="${CSS.escape(cardId)}"]`) ?? null;

  const readGrid = (): Grid => {
    const grid = BOARD_COLUMNS.map(() => [] as string[]);
    const at = new Map<string, number>(BOARD_COLUMNS.map(({ id }, index) => [id, index]));
    for (const element of pageRef.current?.querySelectorAll<HTMLElement>('.board-card[data-card-id]') ?? []) {
      const column = at.get(element.dataset.column ?? '');
      const cardId = element.dataset.cardId;
      if (column !== undefined && cardId !== undefined) grid[column]?.push(cardId);
    }
    return grid;
  };

  /** Focuses a card and brings it into view; with the drawer opening on it, the drawer keeps the DOM focus. */
  const focusCard = (cardId: string, takeFocus = true): void => {
    setFocusedId(cardId);
    window.requestAnimationFrame(() => {
      const element = cardElement(cardId);
      if (element === null) return;
      if (takeFocus) element.focus({ preventScroll: true });
      element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };

  // Selecting several cards (US-028). Kept by id, so it survives a poll; a
  // card that left the board leaves the selection. A card the filters hide
  // stays selected but out of the bar and out of every action.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  /** The card last toggled: where a Shift+click range starts. */
  const anchorRef = useRef<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState<BulkAction | null>(null);
  useEffect(() => {
    if (board !== null) setSelected((previous) => pruneSelection(previous, board.cards));
  }, [board]);
  const selectedCards = boardOrder.filter((card) => selected.has(card.cardId));

  const selectCard = useCallback(
    (cardId: string, mode: 'toggle' | 'range'): void => {
      if (mode === 'range') {
        const column = readGrid().find((list) => list.includes(cardId)) ?? [];
        const range = rangeInColumn(column, anchorRef.current, cardId);
        if (range !== null) {
          setSelected((previous) => new Set([...previous, ...range]));
          return;
        }
      }
      anchorRef.current = cardId;
      setSelected((previous) => toggleSelected(previous, cardId));
    },
    // readGrid reads the DOM through a ref; it never goes stale.
    [],
  );
  const cardSelection = useMemo(() => ({ selected, onSelect: selectCard }), [selected, selectCard]);

  /**
   * Runs a bulk action on the selected cards on show, one at a time, and says
   * how it went in one toast. The selection stays: done cards that left the
   * board (an approved idea) drop out on the read that follows.
   */
  const runBulkAction = (action: BulkAction, input: BulkInput): void => {
    if (bulkRunning !== null) return;
    const targets = selectedCards;
    setBulkRunning(action);
    void (async () => {
      try {
        const outcome = await runBulk(action, targets, input, {
          busy: (card) => runningRef.current.has(card.cardId),
          around: async (card, work) => {
            markRunning(card.cardId, true);
            try {
              return await work();
            } finally {
              markRunning(card.cardId, false);
            }
          },
        });
        await refreshAll();
        if (outcome.skipped === 0 && outcome.left.length === 0) toast.ok(bulkSummary(outcome, action));
        else toast.warn(bulkSummary(outcome, action));
      } catch (cause: unknown) {
        toast.error(describeError(cause));
      } finally {
        setBulkRunning(null);
      }
    })();
  };

  /**
   * A key does nothing while a dialog or the Move menu is open (`false`
   * leaves the key to them), nor before the board has a card.
   */
  const onKey =
    (run: (card: BoardCard | undefined) => unknown) =>
    (): unknown => {
      if (document.querySelector('dialog[open], .card-menu') !== null) return false;
      return run(focusedCard);
    };
  /** A key that walks the grid; `null` is nowhere to go. */
  const walk = (pick: (grid: Grid, cardId: string | null) => string | null) =>
    onKey(() => {
      const next = pick(readGrid(), focusId);
      if (next !== null) focusCard(next);
    });
  /** The arrows scroll a drawer that has the focus, rather than walk the board under it. */
  const arrow = (pick: (grid: Grid, cardId: string | null) => string | null) => {
    const step = walk(pick);
    return (): unknown => (document.activeElement?.closest('.board-drawer') == null ? step() : false);
  };

  /** A key about the cards on the board: the calendar leaves it alone. */
  const boardKey =
    (run: () => unknown) =>
    (): unknown =>
      calendar ? false : run();
  /** A calendar key (`[`, `]`, `t`): the board leaves it alone. */
  const calendarKey = (run: () => void) =>
    onKey(() => {
      if (!calendar) return false;
      run();
      return true;
    });

  const down = (grid: Grid, cardId: string | null): string | null => stepInColumn(grid, cardId, 1);
  const up = (grid: Grid, cardId: string | null): string | null => stepInColumn(grid, cardId, -1);
  const left = (grid: Grid, cardId: string | null): string | null => stepSideways(grid, cardId, -1);
  const right = (grid: Grid, cardId: string | null): string | null => stepSideways(grid, cardId, 1);

  useKeyChords({
    j: boardKey(walk(down)),
    k: boardKey(walk(up)),
    h: boardKey(walk(left)),
    l: boardKey(walk(right)),
    arrowdown: boardKey(arrow(down)),
    arrowup: boardKey(arrow(up)),
    arrowleft: boardKey(arrow(left)),
    arrowright: boardKey(arrow(right)),
    ...Object.fromEntries(
      BOARD_COLUMNS.map((_, index) => [String(index + 1), boardKey(walk((grid) => firstOfColumn(grid, index)))]),
    ),
    // Enter on a card is the card's own (Card.tsx), and on a button or link is
    // theirs: this is Enter with nothing focused, after a click on the page.
    enter: boardKey(
      onKey((card) => {
        if (card === undefined || (document.activeElement !== null && document.activeElement !== document.body)) {
          return false;
        }
        openCard(card.cardId);
        return true;
      }),
    ),
    o: boardKey(
      onKey((card) => {
        if (card !== undefined) navigate(cardHref(card));
      }),
    ),
    a: boardKey(
      onKey((card) => {
        if (card !== undefined) runAction(card);
      }),
    ),
    m: boardKey(
      onKey((card) => {
        if (card !== undefined) cardElement(card.cardId)?.querySelector<HTMLButtonElement>('.board-card__menu')?.click();
      }),
    ),
    e: boardKey(
      onKey((card) => {
        if (card === undefined) return;
        openCard(card.cardId);
        setCampaignFocus({ cardId: card.cardId, token: Date.now() });
      }),
    ),
    '/': onKey(() => searchRef.current?.focus()),
    '.': boardKey(
      onKey(() => {
        const needs = new Set(displayed.filter(needsYou).map((card) => card.cardId));
        const next = nextNeedingYou(readGrid(), focusId, (cardId) => needs.has(cardId));
        if (next === null) {
          toast.info(wording.BOARD_NOTHING_NEEDS_YOU);
          return;
        }
        openCard(next);
        focusCard(next, false);
      }),
    ),
    '?': onKey(() => {
      setShowKeys(true);
    }),
    n: boardKey(
      onKey(() => {
        setQuickAdd(true);
      }),
    ),
    x: boardKey(
      onKey((card) => {
        if (card !== undefined) selectCard(card.cardId, 'toggle');
      }),
    ),
    v: onKey(() => {
      setView(calendar ? 'board' : 'calendar');
    }),
    '[': calendarKey(() => {
      setPeriod(stepPeriod(period, -1));
    }),
    ']': calendarKey(() => {
      setPeriod(stepPeriod(period, 1));
    }),
    t: calendarKey(() => {
      setPeriod(periodOf(period.mode, new Date()));
    }),
    // An open drawer is closed by its own Esc (Drawer.tsx), which runs after this one.
    // The quick-add composer handles its own Esc while the focus is in it; this is the rest of the page.
    escape: onKey(() => {
      if (quickAdd && openCardId === null) {
        setQuickAdd(false);
        return true;
      }
      if (openCardId !== null || selected.size === 0) return false;
      setSelected(new Set());
      return true;
    }),
  });
  const cardFocus = useMemo(() => ({ focusedId: focusId, onFocus: setFocusedId }), [focusId]);

  const [thumbnails, setThumbnails] = useState(readThumbnails);
  // One read of every poster on show, and none at all with Thumbnails off.
  const posters = useAssetDeliveries(
    thumbnails && cards !== null
      ? cards.flatMap((card) =>
          card.type === 'content' && card.posterAssetId !== null
            ? [{ contentId: card.content.id, assetId: card.posterAssetId }]
            : [],
        )
      : [],
  );

  // The subtitle is about the whole board, whatever the filters hide.
  const subtitle =
    board === null
      ? undefined
      : wording.boardSubtitle(
          board.cards.filter(needsYou).length,
          board.cards.filter((card) => card.column === 'production').length,
          board.cards.reduce((sum, card) => sum + (card.type === 'content' ? card.content.posts.due : 0), 0),
        );

  const activeCampaigns = (board?.campaigns ?? []).filter((campaign) => campaign.archivedAt === null);
  const archivedCampaigns = (board?.campaigns ?? []).filter((campaign) => campaign.archivedAt !== null);
  // New work starts in the campaign the board is narrowed to, when it is one (US-030).
  const campaignQuery =
    filters.campaign === 'all' || filters.campaign === 'none' ? '' : `?campaign=${encodeURIComponent(filters.campaign)}`;
  const newContentHref = `/content/new${campaignQuery}`;
  const newIdeasHref = `/ideas/new${campaignQuery}`;

  // Quick add (US-029): the plus in the Ideas header, or `n`, opens it at the top of the column.
  const ideasComposer: IdeasComposer = {
    onOpen: () => {
      setQuickAdd(true);
    },
    composer: quickAdd ? (
      <QuickAdd
        repositories={repositories}
        campaigns={activeCampaigns}
        campaignFilter={filters.campaign}
        onAdded={(count) => {
          toast.ok(wording.quickAdded(count));
          void refreshAll();
        }}
        onClose={() => {
          setQuickAdd(false);
        }}
      />
    ) : null,
  };

  return (
    <CardActionsContext.Provider value={cardActions}>
      <CardFocusContext.Provider value={cardFocus}>
      <CardSelectionContext.Provider value={cardSelection}>
      <div className="page page--full" ref={pageRef}>
        <PageHeader
          title={wording.BOARD}
          {...(subtitle === undefined ? {} : { subtitle })}
          actions={
            <>
              <Segmented
                ariaLabel={wording.BOARD_VIEW}
                value={calendar ? 'calendar' : 'board'}
                onChange={setView}
                options={[
                  { value: 'board', label: wording.BOARD_VIEW_BOARD },
                  { value: 'calendar', label: wording.BOARD_VIEW_CALENDAR },
                ]}
              />
              <button
                type="button"
                className="button"
                disabled={board === null}
                onClick={() => {
                  setShowCampaigns(true);
                }}
              >
                <Icon name="project" />
                {wording.CAMPAIGNS}
              </button>
              <Link className="button" href={newIdeasHref}>
                <Icon name="bulb" />
                {wording.BOARD_NEW_IDEAS}
              </Link>
              <Link className="button button--primary" href={newContentHref}>
                <Icon name="plus" />
                {wording.BOARD_NEW_CONTENT}
              </Link>
            </>
          }
        />

        {error !== null && (
          <div className="notice notice--error" role="alert">
            <Icon name="x-circle" />
            <div className="notice__body">
              {wording.BOARD_READ_FAILED} {error}
            </div>
          </div>
        )}

        {board === null || cards === null || columns === null ? (
          error === null && (
            <div className="panel">
              <div className="panel__body">
                <Skeleton lines={5} />
              </div>
            </div>
          )
        ) : (
          // The toolbar stays even on an empty board: everything may be archived
          // or posted long ago, and Archived and All posted are how to see it.
          <>
            <div className="toolbar board__toolbar">
              <select
                className="field__input field__input--inline"
                value={filters.campaign}
                onChange={(event) => {
                  setParam('campaign', event.target.value);
                }}
                aria-label={wording.BOARD_FILTER_CAMPAIGN}
              >
                <option value="all">{wording.BOARD_ALL_CAMPAIGNS}</option>
                <option value="none">{wording.BOARD_NO_CAMPAIGN}</option>
                {activeCampaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
                {archivedCampaigns.length > 0 && (
                  <optgroup label={wording.BOARD_ARCHIVED_CAMPAIGNS}>
                    {archivedCampaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <select
                className="field__input field__input--inline"
                value={filters.book}
                onChange={(event) => {
                  setParam('book', event.target.value);
                }}
                aria-label={wording.BOARD_FILTER_BOOK}
              >
                <option value="all">{wording.BOARD_ALL_BOOKS}</option>
                {board.brandBooks.map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.name}
                  </option>
                ))}
              </select>
              <select
                className="field__input field__input--inline"
                value={filters.kind}
                onChange={(event) => {
                  setParam('kind', event.target.value);
                }}
                aria-label={wording.BOARD_FILTER_KIND}
              >
                <option value="all">{wording.BOARD_ALL_KINDS}</option>
                {CONTENT_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind === 'video' ? 'Video' : 'Images'}
                  </option>
                ))}
              </select>
              <select
                className="field__input field__input--inline"
                value={filters.funnel}
                onChange={(event) => {
                  setParam('funnel', event.target.value);
                }}
                aria-label={wording.BOARD_FILTER_FUNNEL}
              >
                <option value="all">{wording.BOARD_ALL_FUNNEL_STAGES}</option>
                {FUNNEL_STAGES.map((stage) => (
                  <option key={stage} value={stage}>
                    {FUNNEL_LABEL[stage]}
                  </option>
                ))}
              </select>
              {!calendar && (
                <Segmented
                  ariaLabel={wording.BOARD_SHOW}
                  value={filters.show}
                  onChange={(value) => {
                    setParam('show', value);
                  }}
                  options={BOARD_SHOWS}
                />
              )}
              <label className="search">
                <Icon name="search" />
                <input
                  ref={searchRef}
                  className="search__input"
                  type="search"
                  placeholder={wording.BOARD_SEARCH_PLACEHOLDER}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                  }}
                  aria-label={wording.BOARD_SEARCH}
                />
                <Kbd>/</Kbd>
              </label>
              {!calendar && (
                <>
                  <label className="board__group">
                    {wording.BOARD_GROUP}
                    <select
                      className="field__input field__input--inline"
                      value={group ?? ''}
                      onChange={(event) => {
                        setParam('group', event.target.value);
                      }}
                    >
                      {BOARD_GROUPS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={thumbnails}
                      onChange={(event) => {
                        setThumbnails(event.target.checked);
                        writeThumbnails(event.target.checked);
                      }}
                    />
                    {wording.BOARD_THUMBNAILS}
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={filters.postedAll}
                      onChange={(event) => {
                        setParam('posted', event.target.checked ? 'all' : '');
                      }}
                    />
                    {wording.BOARD_ALL_POSTED}
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={filters.archived}
                      onChange={(event) => {
                        setParam('archived', event.target.checked ? '1' : '');
                      }}
                    />
                    {wording.BOARD_ARCHIVED}
                  </label>
                  <p className="board__keys">
                    <Kbd>j</Kbd> <Kbd>k</Kbd> move · <Kbd>Enter</Kbd> open · <Kbd>a</Kbd> act · <Kbd>m</Kbd> move to ·{' '}
                    <Kbd>.</Kbd> next that needs you · <Kbd>?</Kbd> all keys
                  </p>
                </>
              )}
              {calendar && (
                <p className="board__keys">
                  <Kbd>[</Kbd> <Kbd>]</Kbd> previous, next · <Kbd>t</Kbd> today · <Kbd>v</Kbd> board ·{' '}
                  <Kbd>?</Kbd> all keys
                </p>
              )}
              <NotifyBell />
            </div>
            {calendar ? (
              <Calendar
                period={period}
                onPeriod={(delta) => {
                  setPeriod(stepPeriod(period, delta));
                }}
                onMode={(mode) => {
                  setPeriod(switchMode(period, mode, new Date()));
                }}
                onToday={() => {
                  setPeriod(periodOf(period.mode, new Date()));
                }}
                keep={keepOnCalendar}
                openCardId={openCardId}
                onOpen={openCard}
                refreshToken={calendarToken}
                toPost={toPostOnCalendar}
                onSchedule={(card, day) => {
                  if (!runningRef.current.has(card.cardId)) setDialog({ dialog: 'schedule', card, day });
                }}
                onChanged={refreshAll}
              />
            ) : (
              <>
                {/* With no column to stand in, the composer stands above the empty board. */}
                {cards.length === 0 && ideasComposer.composer !== null && (
                  <div className="board__quick-add-alone">{ideasComposer.composer}</div>
                )}
                {board.cards.length === 0 && !filtersNarrow(filters) ? (
                  <EmptyState
                    icon="project"
                    title={wording.BOARD_EMPTY}
                    action={
                      <>
                        <Link className="button button--primary" href={newContentHref}>
                          <Icon name="plus" />
                          {wording.BOARD_NEW_CONTENT}
                        </Link>
                        <Link className="button" href={newIdeasHref}>
                          <Icon name="bulb" />
                          {wording.BOARD_NEW_IDEAS}
                        </Link>
                        <button type="button" className="button" onClick={ideasComposer.onOpen}>
                          <Icon name="plus" />
                          {wording.QUICK_ADD}
                        </button>
                      </>
                    }
                  >
                    {wording.BOARD_EMPTY_BODY}
                  </EmptyState>
                ) : cards.length === 0 ? (
                  <EmptyState
                    icon="filter"
                    title={wording.BOARD_NO_MATCH}
                    action={
                      <>
                        <button type="button" className="button" onClick={clearFilters}>
                          {wording.BOARD_CLEAR_FILTERS}
                        </button>
                        <button type="button" className="button" onClick={ideasComposer.onOpen}>
                          <Icon name="plus" />
                          {wording.QUICK_ADD}
                        </button>
                      </>
                    }
                  />
                ) : group !== null && lanes !== null ? (
                  <Lanes
                    {...dragHandlers}
                    dropTarget={dropTarget}
                    group={group}
                    lanes={lanes}
                    columns={columns}
                    now={now}
                    campaigns={campaigns}
                    posters={posters}
                    openCardId={openCardId}
                    onOpen={openCard}
                    ideasComposer={ideasComposer}
                    productionMeta={
                      stats === null ? undefined : wording.productionSlots(stats.productions.active, stats.productions.max)
                    }
                  />
                ) : (
                  <div className="board" {...dragHandlers}>
                    {BOARD_COLUMNS.map(({ id, label }) => (
                      <Column
                        key={id}
                        drop={dropTarget(id, null, columns[id])}
                        id={id}
                        label={label}
                        cards={columns[id]}
                        now={now}
                        campaigns={campaigns}
                        posters={posters}
                        openCardId={openCardId}
                        onOpen={openCard}
                        {...(id === 'ideas' ? { ideasComposer } : {})}
                        meta={
                          id === 'production' && stats !== null
                            ? wording.productionSlots(stats.productions.active, stats.productions.max)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {board !== null && !calendar && selectedCards.length > 0 && (
          <BulkBar
            cards={selectedCards}
            campaigns={activeCampaigns}
            running={bulkRunning}
            onRun={runBulkAction}
            onClear={() => {
              setSelected(new Set());
            }}
          />
        )}
        {board !== null && openCardId !== null && (
          <Drawer
            cardId={openCardId}
            card={board.cards.find((card) => card.cardId === openCardId)}
            campaigns={board.campaigns}
            showsArchived={filters.archived}
            {...(planningFocus?.cardId === openCardId ? { planningFocus: planningFocus.token } : {})}
            {...(campaignFocus?.cardId === openCardId ? { campaignFocus: campaignFocus.token } : {})}
            onClose={closeCard}
            onOpen={openCard}
            onChanged={refreshAll}
          />
        )}
        <ActionDialogs
          open={dialog}
          onClose={() => {
            setDialog(null);
          }}
          onDone={async () => {
            await refreshAll();
            dialog?.onMoved?.();
          }}
        />
        {duplicating !== null && (
          <DuplicateDialog
            open
            contentId={duplicating.content.id}
            sourceName={duplicating.content.name}
            onCancel={() => {
              setDuplicating(null);
            }}
            onDuplicated={(id) => {
              setDuplicating(null);
              openCard(`content:${id}`);
              void refreshAll();
            }}
          />
        )}
        {board !== null && (
          <CampaignsDialog
            open={showCampaigns}
            campaigns={board.campaigns}
            brandBooks={board.brandBooks}
            onClose={() => {
              setShowCampaigns(false);
            }}
            onChanged={refreshAll}
          />
        )}
        <ShortcutsDialog
          open={showKeys}
          onClose={() => {
            setShowKeys(false);
          }}
        />
        <div className="visually-hidden" aria-live="polite">
          {announcement}
        </div>
      </div>
      </CardSelectionContext.Provider>
      </CardFocusContext.Provider>
    </CardActionsContext.Provider>
  );
}

/**
 * What dropping the dragged card on a column (and lane) would do (US-025):
 * nothing (its own place), a refusal with the reason, or a move — a campaign
 * lane to change to first, then the column move, either of which may be absent.
 */
type Verdict =
  | { readonly kind: 'same' }
  | { readonly kind: 'refused'; readonly reason: string }
  | { readonly kind: 'move'; readonly lane: Lane | null; readonly move: Extract<Move, { kind: 'action' }> | null };

/** A column or lane cell as a place to drop a card; `state` is `null` while nothing is dragged over the board. */
interface DropTarget {
  readonly state: 'ok' | 'no' | null;
  /** Where the insertion line is drawn in its list (before that card; the length is the end), if anywhere. */
  readonly insertAt: number | null;
  readonly onDragOver: (event: DragEvent<HTMLElement>) => void;
  readonly onDragLeave: (event: DragEvent<HTMLElement>) => void;
  readonly onDrop: (event: DragEvent<HTMLElement>) => void;
}

function dropClass(drop: DropTarget): string {
  if (drop.state === null) return '';
  return drop.state === 'ok' ? ' board__column--drop-ok' : ' board__column--drop-no';
}

function insertClass(insertAt: number | null, index: number, count: number): string | undefined {
  if (insertAt === index) return 'board__slot--before';
  if (insertAt === count && index === count - 1) return 'board__slot--after';
  return undefined;
}

/** The Ideas column's quick add (US-029): what its plus button does, and the composer while it is open. */
interface IdeasComposer {
  readonly onOpen: () => void;
  readonly composer: ReactNode;
}

function columnLabel(column: BoardColumn): string {
  return BOARD_COLUMNS.find(({ id }) => id === column)?.label ?? column;
}

/** The last address `/board` was left at, without its `?`. */
const LAST_SEARCH_KEY = 'mc.board.lastSearch';

/** Storage can throw (a private window, a full quota); nothing is remembered then. */
function readLastSearch(): string | null {
  try {
    return window.localStorage.getItem(LAST_SEARCH_KEY);
  } catch {
    return null;
  }
}

/** The open card is left out: a bare `/board` comes back to the view, not to a drawer. */
function writeLastSearch(search: string): void {
  const view = new URLSearchParams(search);
  view.delete('card');
  try {
    window.localStorage.setItem(LAST_SEARCH_KEY, view.toString());
  } catch {
    // Not remembered this time; the address still holds the view.
  }
}

/**
 * The search box's first text when a bare `/board` is about to be restored,
 * so the debounced write does not wipe the remembered `q` a beat later.
 */
function readLastQuery(search: string): string {
  if (search !== '') return '';
  return new URLSearchParams(readLastSearch() ?? '').get('q') ?? '';
}

/**
 * The switch for "needs you" notifications (US-034). It asks the browser on
 * the way on. A browser that has blocked them leaves it inert, and its title
 * says so (`aria-disabled` rather than `disabled`, so the title still shows).
 */
function NotifyBell() {
  const { on, permission, toggle } = useNotifySwitch();
  const unavailable = permission === 'denied' || permission === 'unsupported';
  const title =
    permission === 'denied'
      ? wording.NOTIFY_BLOCKED
      : permission === 'unsupported'
        ? wording.NOTIFY_UNSUPPORTED
        : on
          ? wording.NOTIFY_ON
          : wording.NOTIFY_OFF;
  return (
    <button
      type="button"
      className={`button button--quiet button--icon board__notify${on ? ' button--active' : ''}`}
      onClick={toggle}
      aria-disabled={unavailable}
      aria-pressed={on}
      aria-label={wording.NOTIFY_LABEL}
      title={title}
    >
      <Icon name="bell" />
    </button>
  );
}

/** Whether cards show their poster; remembered per browser, on until switched off. */
const THUMBNAILS_KEY = 'mc.board.thumbnails';

/** Storage can throw (a private window, a full quota); the default stands in. */
function readThumbnails(): boolean {
  try {
    return window.localStorage.getItem(THUMBNAILS_KEY) !== 'off';
  } catch {
    return true;
  }
}

function writeThumbnails(on: boolean): void {
  try {
    window.localStorage.setItem(THUMBNAILS_KEY, on ? 'on' : 'off');
  } catch {
    // Not remembered this time; the cards still follow the box.
  }
}

function Column({
  id,
  drop,
  label,
  cards,
  meta,
  now,
  campaigns,
  posters,
  openCardId,
  onOpen,
  ideasComposer,
}: {
  readonly id: BoardColumn;
  readonly drop: DropTarget;
  readonly label: string;
  readonly cards: readonly BoardCard[];
  readonly meta: string | undefined;
  readonly now: number;
  readonly campaigns: ReadonlyMap<string, Campaign>;
  readonly posters: ReadonlyMap<string, AssetDelivery>;
  readonly openCardId: string | null;
  readonly onOpen: (cardId: string) => void;
  /** The Ideas column only: its plus button and quick-add composer. */
  readonly ideasComposer?: IdeasComposer;
}) {
  const headingId = `board-column-${id}`;
  return (
    <section
      className={`board__column${dropClass(drop)}`}
      aria-labelledby={headingId}
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      <ColumnHeader id={headingId} label={label} count={cards.length} meta={meta} onAdd={ideasComposer?.onOpen} />
      {ideasComposer?.composer}
      <CardList
        cards={cards}
        insertAt={drop.insertAt}
        now={now}
        campaigns={campaigns}
        posters={posters}
        campaignLane={false}
        openCardId={openCardId}
        onOpen={onOpen}
      />
    </section>
  );
}

function ColumnHeader({
  id,
  label,
  count,
  meta,
  onAdd,
}: {
  readonly id: string;
  readonly label: string;
  readonly count: number;
  readonly meta: string | undefined;
  /** Shows a plus button that adds to the column (the Ideas column's quick add). */
  readonly onAdd?: (() => void) | undefined;
}) {
  return (
    <header className="board__column-header">
      <h2 className="board__column-title" id={id}>
        {label}
        <span className="board__count">{count}</span>
      </h2>
      {meta !== undefined && <span className="board__column-meta">{meta}</span>}
      {onAdd !== undefined && (
        <button
          type="button"
          className="button button--small button--quiet button--icon board__column-add"
          onClick={onAdd}
          aria-label={wording.QUICK_ADD}
          title={wording.QUICK_ADD_OPEN}
        >
          <Icon name="plus" />
        </button>
      )}
    </header>
  );
}

function CardList({
  cards,
  insertAt,
  now,
  campaigns,
  posters,
  campaignLane,
  openCardId,
  onOpen,
}: {
  readonly cards: readonly BoardCard[];
  /** The insertion line: before the card at this index, or after the last at the length. */
  readonly insertAt: number | null;
  readonly now: number;
  readonly campaigns: ReadonlyMap<string, Campaign>;
  readonly posters: ReadonlyMap<string, AssetDelivery>;
  readonly campaignLane: boolean;
  readonly openCardId: string | null;
  readonly onOpen: (cardId: string) => void;
}) {
  if (cards.length === 0) return <p className="board__empty">{wording.BOARD_COLUMN_EMPTY}</p>;
  return (
    <ol className="board__cards">
      {cards.map((card, index) => (
        <li key={card.cardId} className={insertClass(insertAt, index, cards.length)}>
          <Card
            card={card}
            now={now}
            campaigns={campaigns}
            campaignLane={campaignLane}
            open={card.cardId === openCardId}
            onOpen={() => {
              onOpen(card.cardId);
            }}
            poster={
              card.type === 'content' && card.posterAssetId !== null ? posters.get(card.posterAssetId) : undefined
            }
          />
        </li>
      ))}
    </ol>
  );
}

/** Whether a lane is folded away; remembered per browser, per grouping. */
function laneKey(group: BoardGroup, laneId: string): string {
  return `mc.board.lane.${group}.${laneId}`;
}

/** Storage can throw (a private window, a full quota); the lane is open then. */
function readLaneCollapsed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeLaneCollapsed(key: string, collapsed: boolean): void {
  try {
    if (collapsed) window.localStorage.setItem(key, '1');
    else window.localStorage.removeItem(key);
  } catch {
    // Not remembered this time; the lane still folds.
  }
}

/**
 * The board cut into swimlanes (US-017): one row of seven cells per lane,
 * under one row of column headers that stays at the top while the lanes
 * scroll beneath it. The strip scrolls both ways on its own, so the headers
 * have something to stick to.
 */
function Lanes({
  onDragStart,
  onDragEnd,
  dropTarget,
  group,
  lanes,
  columns,
  now,
  campaigns,
  posters,
  openCardId,
  onOpen,
  ideasComposer,
  productionMeta,
}: {
  readonly onDragStart: (event: DragEvent<HTMLElement>) => void;
  readonly onDragEnd: (event: DragEvent<HTMLElement>) => void;
  readonly dropTarget: (column: BoardColumn, lane: Lane, list: readonly BoardCard[]) => DropTarget;
  readonly group: BoardGroup;
  readonly lanes: readonly Lane[];
  readonly columns: Record<BoardColumn, BoardCard[]>;
  readonly now: number;
  readonly campaigns: ReadonlyMap<string, Campaign>;
  readonly posters: ReadonlyMap<string, AssetDelivery>;
  readonly openCardId: string | null;
  readonly onOpen: (cardId: string) => void;
  readonly ideasComposer: IdeasComposer;
  readonly productionMeta: string | undefined;
}) {
  // What was toggled this visit; anything else is read from storage.
  const [toggled, setToggled] = useState<Readonly<Record<string, boolean>>>({});
  const isCollapsed = (key: string): boolean => toggled[key] ?? readLaneCollapsed(key);

  return (
    <div className="board board--lanes" onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="board__lanes">
        <div className="board__lane-heads">
          {BOARD_COLUMNS.map(({ id, label }) => (
            <div key={id} className="board__lane-head">
              <ColumnHeader
                id={`board-column-${id}`}
                label={label}
                count={columns[id].length}
                meta={id === 'production' ? productionMeta : undefined}
                onAdd={id === 'ideas' ? ideasComposer.onOpen : undefined}
              />
              {id === 'ideas' && ideasComposer.composer}
            </div>
          ))}
        </div>
        {lanes.map((lane) => {
          const key = laneKey(group, lane.id);
          const collapsed = isCollapsed(key);
          const headingId = `board-lane-${lane.id}`;
          const range =
            lane.campaign === null
              ? null
              : wording.campaignRange(
                  lane.campaign.startsOn === null ? null : shortDay(lane.campaign.startsOn),
                  lane.campaign.endsOn === null ? null : shortDay(lane.campaign.endsOn),
                );
          return (
            <section
              key={lane.id}
              className={`board__lane${collapsed ? ' board__lane--collapsed' : ''}`}
              aria-labelledby={headingId}
            >
              <header className="board__lane-header">
                <h3 className="board__lane-title" id={headingId}>
                  <button
                    type="button"
                    className="board__lane-toggle"
                    aria-expanded={!collapsed}
                    title={collapsed ? wording.BOARD_EXPAND_LANE : wording.BOARD_COLLAPSE_LANE}
                    onClick={() => {
                      writeLaneCollapsed(key, !collapsed);
                      setToggled((previous) => ({ ...previous, [key]: !collapsed }));
                    }}
                  >
                    <Icon name={collapsed ? 'chevron-right' : 'chevron-down'} />
                    <span className="board__lane-dot" style={{ backgroundColor: lane.color }} aria-hidden="true" />
                    <span className="board__lane-name">{lane.name}</span>
                    <span className="board__count">{lane.count}</span>
                  </button>
                </h3>
                {lane.campaign !== null && (
                  <span className="board__column-meta">
                    {wording.campaignPosted(lane.campaign.counts.posted, lane.campaign.counts.contents)}
                  </span>
                )}
                {range !== null && <span className="board__column-meta">{range}</span>}
              </header>
              {collapsed ? (
                <p className="board__lane-row board__lane-counts">
                  {BOARD_COLUMNS.map(({ id, label }) => (
                    <span key={id} className="board__lane-count">
                      {label} <span className="board__count">{lane.columns[id].length}</span>
                    </span>
                  ))}
                </p>
              ) : (
                <div className="board__lane-row">
                  {BOARD_COLUMNS.map(({ id, label }) => {
                    const drop = dropTarget(id, lane, lane.columns[id]);
                    return (
                    <div
                      key={id}
                      className={`board__column board__cell${dropClass(drop)}`}
                      role="group"
                      aria-label={`${lane.name} · ${label}`}
                      onDragOver={drop.onDragOver}
                      onDragLeave={drop.onDragLeave}
                      onDrop={drop.onDrop}
                    >
                      <CardList
                        cards={lane.columns[id]}
                        insertAt={drop.insertAt}
                        now={now}
                        campaigns={campaigns}
                        posters={posters}
                        campaignLane={group === 'campaign'}
                        openCardId={openCardId}
                        onOpen={onOpen}
                      />
                    </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
